import { useAppDispatch } from 'app/hooks';
import { useForm, ValidationErrorsType, ValidationErrorType } from 'hooks/useForm';
import { useRole } from 'hooks/useRole';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlaceConfigData } from 'types/dtos';
import { useAddNestedProductMutation } from '../services';
import { useGetPlacesQuery } from 'features/place/services';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useGetNestedProductCategoriesQuery } from 'features/nested-product-category/services';
import { NESTED_PRODUCT_FIELDS_SCHEMA, NESTED_PRODUCT_INITIAL_FIELDS } from '../constants/nested-product-fields';
import { handleConfigFieldChange } from 'utils/products-config-price-helper';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { removeEmpty, trimValues } from 'utils/object-helpers';
import { NestedProductDto } from '../types/dtos';
import { validateConfigFields } from 'utils/products-config-validation-helper';
import { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from 'hooks/useWorkspace';
import { NESTED_PRODUCT_STATUS } from '../constants/status';

type AllErrorsType<T> = ValidationErrorsType<T> & {
    [key: string]: ValidationErrorType | undefined;
};

const useController = () => {
    const { placeId = '' } = useParams();
    const [individualPrices, setIndividualPrices] = useState(false);
    const [placeConfigData, setPlaceConfigData] = useState<PlaceConfigData[]>([]);
    const [priceErrors, setPriceErrors] = useState<Record<string, ValidationErrorType>>({});
    const { isAdmin } = useRole();
    const { websiteId } = useWorkspace();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [addNestedProduct, { isLoading: isAddNestedProductLoading }] = useAddNestedProductMutation();
    const { data: places, isLoading: isGetPlacesLoading } = useGetPlacesQuery({
        ...FETCH_ALL_PARAMS,
        website_id: websiteId,
    });
    const { data: nestedProductCategories, isLoading: isGetNestedProductCategoriesLoading } =
        useGetNestedProductCategoriesQuery(FETCH_ALL_PARAMS);
    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({ defaultValues: NESTED_PRODUCT_INITIAL_FIELDS, validationSchema: NESTED_PRODUCT_FIELDS_SCHEMA });

    const allErrors: AllErrorsType<typeof NESTED_PRODUCT_INITIAL_FIELDS> = useMemo(() => {
        return {
            ...errors,
            ...priceErrors,
        };
    }, [errors, priceErrors]);

    useEffect(() => {
        if (places?.content && placeId) {
            const initialPlace = places.content.find((place) => place.id === placeId);
            if (initialPlace) {
                setPlaceConfigData([
                    {
                        placeId: initialPlace.id,
                        placeName: initialPlace.name,
                        price: '',
                        discountedPrice: '',
                        isAvailable: `${NESTED_PRODUCT_STATUS.AVAILABLE}`,
                    },
                ]);
            }
        }
    }, [places, placeId]);

    const handlePlaceConfigSelectChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value },
            } = event;
            const selectedIds = typeof value === 'string' ? value.split(',') : value;

            if (places?.content) {
                const newPlaceProductConfig = places.content
                    .filter((place) => selectedIds.includes(place.id))
                    .map((place) => {
                        const existing = placeConfigData.find((config) => config.placeId === place.id);
                        return (
                            existing || {
                                placeId: place.id,
                                placeName: place.name,
                                price: '',
                                discountedPrice: '',
                                isAvailable: `${NESTED_PRODUCT_STATUS.AVAILABLE}`,
                            }
                        );
                    });

                setPlaceConfigData(newPlaceProductConfig);
            }
        },
        [places?.content, placeConfigData, setPlaceConfigData],
    );

    const validatePrices = useCallback(
        (configs: PlaceConfigData[]): Record<string, ValidationErrorType> => {
            return validateConfigFields<PlaceConfigData>(configs, 'placeId', ['price', 'discountedPrice'], {
                price: {
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_POSITIVE'),
                    },
                },
                discountedPrice: {
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_POSITIVE'),
                    },
                    custom: [
                        {
                            validator: (value, allValues) =>
                                !allValues.price.trim() || Number(value) < Number(allValues.price),
                            message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_LOWER_THAN_PRICE'),
                        },
                    ],
                },
            });
        },
        [t],
    );

    const handlePlaceConfigPriceChange = useCallback(
        (field: string, value: string, placeId?: string) => {
            handleConfigFieldChange({
                field,
                value,
                id: placeId,
                currentData: placeConfigData,
                setData: setPlaceConfigData,
                errorState: priceErrors,
                setErrorState: setPriceErrors,
                validateFn: validatePrices,
                errorPrefixes: ['price-', 'discountedPrice-'],
            });
        },
        [placeConfigData, priceErrors, setPlaceConfigData, setPriceErrors, validatePrices],
    );

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'nestedProduct.NESTED_PRODUCT_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = { ...formData };

        const placeConfigsPayload = placeConfigData.map((config) => {
            const { placeId, isAvailable, price, discountedPrice } = config;
            return {
                placeId,
                price: price === '' ? null : Number(price),
                discountedPrice: discountedPrice ? Number(discountedPrice) : null,
                isAvailable: isAvailable === `${NESTED_PRODUCT_STATUS.AVAILABLE}` ? true : false,
            };
        });
        data.placeConfigs = placeConfigsPayload;
        data.websiteId = websiteId;

        return trimValues(removeEmpty(data)) as NestedProductDto;
    }, [formData, placeConfigData, websiteId]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await addNestedProduct(payload).unwrap();

                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [getPayload, handleErrors, handleSubmit, handleSuccess, navigate, addNestedProduct],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData,
        places,
        nestedProductCategories,
        onSubmit,
        onCancel,
        onChange,
        isLoading: isGetPlacesLoading || isGetNestedProductCategoriesLoading,
        isAddProductLoading: isAddNestedProductLoading,
        errors: allErrors,
        isAdmin,
        websiteId,
        onSelectConfigChange: handlePlaceConfigSelectChange,
        individualPrices,
        setIndividualPrices,
        placeConfigData,
        onConfigPriceChange: handlePlaceConfigPriceChange,
        placeId,
    };
};
export default useController;
